// components/fournisseurs/SupplierPurchaseHistory.tsx
import { useState, useEffect, useCallback } from 'react';
import { createResource,getResources,deleteResource, updateResource, patchResource} from '../../../router/api';
import toast from 'react-hot-toast';

interface LigneProduit {
  produit: {
    id: string;
    designation: string;
  };
  quantite: number;
}

interface PurchaseOrder {
  id: string;
  numero: string;
  date_commande: string;
  date_livraison_prevue?: string | null;
  total_ttc: number;
  statut: 'BROUILLON' | 'ENVOYE' | 'PARTIEL' | 'LIVRE' | 'ANNULE' | 'ALERTE';
  lignes: LigneProduit[];
}

interface PurchaseHistoryStats {
  total_commandes: number;
  montant_total: number;
  delai_moyen_jours: number;
}

interface SupplierPurchaseHistoryProps {
  supplierId: string;
  supplierName: string;
  supplierRef: string;
  onClose: () => void;
}

export default function SupplierPurchaseHistory({
  supplierId,
  supplierName,
  supplierRef,
  onClose,
}: SupplierPurchaseHistoryProps) {
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [stats, setStats] = useState<PurchaseHistoryStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchHistory = useCallback(async (pageNum = 1) => {
    try {
      setLoading(true);
      const { data } = await getResources(
        `fournisseurs/${supplierId}/commandes`,
        {
          params: { page: pageNum, per_page: 10 },
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }
      );

      setOrders(data.data);
      setStats(data.stats);
      setTotalPages(data.meta.last_page);
      setPage(data.meta.current_page);
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Impossible de charger l'historique");
    } finally {
      setLoading(false);
    }
  }, [supplierId]);

  useEffect(() => {
    fetchHistory(1);
  }, [fetchHistory]);

  const formatPrice = (amount: number) =>
    new Intl.NumberFormat('fr-GN', {
      style: 'currency',
      currency: 'GNF',
      minimumFractionDigits: 0,
    }).format(amount);

  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

    const getStatusBadge = (statut: string) => {
    const config: Record<string, { label: string; bg: string; text: string }> = {
      BROUILLON: { label: 'Brouillon', bg: 'bg-gray-100', text: 'text-gray-800' },
      ENVOYE: { label: 'Envoyé', bg: 'bg-blue-100', text: 'text-blue-800' },
      PARTIEL: { label: 'Partiel', bg: 'bg-orange-100', text: 'text-orange-800' },
      LIVRE: { label: 'Livrée', bg: 'bg-green-100', text: 'text-green-800' },
      ANNULE: { label: 'Annulée', bg: 'bg-red-100', text: 'text-red-800' },
      ALERTE: { label: 'Alerte', bg: 'bg-purple-100', text: 'text-purple-800' },

      
    };
    
  const badge = config[statut.toUpperCase()] || config.BROUILLON;
  return `${badge.bg} ${badge.text}`;
  };
//   const getStatusBadge = (statut: string) => {
//     const map: Record<string, string> = {
//       livrée: 'bg-green-100 text-green-800',
//       en_cours: 'bg-blue-100 text-blue-800',
//       validée: 'bg-purple-100 text-purple-800',
//       en_attente: 'bg-yellow-100 text-yellow-800',
//       annulée: 'bg-red-100 text-red-800',
//     };
//     return map[statut] || 'bg-gray-100 text-gray-800';
//   };

  // Génère une liste lisible des produits
  const getProduitsLabel = (lignes: LigneProduit[]) => {
    if (!lignes || lignes.length === 0) return 'Aucun produit';
    return lignes
      .map(l => `${l.produit.designation} (${l.quantite})`)
      .join(', ');
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Historique des achats</h2>
          <p className="text-sm text-gray-600 mt-1">
            {supplierName} - Réf: {supplierRef}
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <i className="ri-close-line text-2xl" />
        </button>
      </div>

      <div className="p-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {loading ? (
            [...Array(3)].map((_, i) => (
              <div key={i} className="bg-gray-50 p-6 rounded-lg animate-pulse">
                <div className="h-5 bg-gray-200 rounded w-32 mb-3" />
                <div className="h-10 bg-gray-300 rounded w-24" />
              </div>
            ))
          ) : stats ? (
            <>
              <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total commandes</p>
                    <p className="text-4xl font-bold text-blue-600 mt-2">{stats.total_commandes}</p>
                  </div>
                  <i className="ri-shopping-cart-line text-5xl text-blue-500 opacity-70" />
                </div>
              </div>

              <div className="bg-green-50 p-6 rounded-xl border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Montant total</p>
                    <p className="text-4xl font-bold text-green-600 mt-2">
                      {formatPrice(stats.montant_total)}
                    </p>
                  </div>
                  <i className="ri-money-dollar-circle-line text-5xl text-green-500 opacity-70" />
                </div>
              </div>

              <div className="bg-purple-50 p-6 rounded-xl border border-purple-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Délai moyen</p>
                    <p className="text-4xl font-bold text-purple-600 mt-2">
                      {stats.delai_moyen_jours}j
                    </p>
                  </div>
                  <i className="ri-time-line text-5xl text-purple-500 opacity-70" />
                </div>
              </div>
            </>
          ) : null}
        </div>

        {/* Tableau */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-20 bg-gray-50 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-16">
              <i className="ri-inbox-line text-7xl text-gray-300 mb-4" />
              <p className="text-lg text-gray-500">Aucune commande enregistrée</p>
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">N° BC</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Produits</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Montant TTC</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Statut</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {orders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {formatDate(order.date_commande)}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-indigo-600">
                        {order.numero}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 max-w-md">
                        <div className="truncate" title={getProduitsLabel(order.lignes)}>
                          {getProduitsLabel(order.lignes)}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-gray-900">
                        {formatPrice(order.total_ttc)}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-3 py-1.5 text-xs font-semibold rounded-full ${getStatusBadge(order.statut)}`}>
                          {order.statut.charAt(0).toUpperCase() + order.statut.slice(1).replace('_', ' ')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-3 mt-8">
                  <button
                    onClick={() => fetchHistory(page - 1)}
                    disabled={page === 1}
                    className="px-5 py-2.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                  >
                    <i className="ri-arrow-left-s-line" /> Précédent
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {page} sur {totalPages}
                  </span>
                  <button
                    onClick={() => fetchHistory(page + 1)}
                    disabled={page === totalPages}
                    className="px-5 py-2.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                  >
                    Suivant <i className="ri-arrow-right-s-line" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}